import { useEffect, useId, useRef, useState } from "react";

/**
 * The columns this menu can hide, in the order the table renders them. Only the
 * three the table can lose without leaving a section empty: Impressions and
 * Clicks report traffic the rest of the row doesn't depend on, and Continued
 * sales is an addend of Total sales rather than a figure the other columns are
 * derived from.
 */
export type HideableColumn = { key: string; label: string };

function SlidersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M2 4.5h5M10 4.5h4M2 11.5h4M9 11.5h5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
      <circle
        cx="8.5"
        cy="4.5"
        r="1.75"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <circle
        cx="7.5"
        cy="11.5"
        r="1.75"
        stroke="currentColor"
        strokeWidth="1.25"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M9 4.25c3.25 0 5.75 2.5 6.75 4.75-1 2.25-3.5 4.75-6.75 4.75S3.25 11.25 2.25 9C3.25 6.75 5.75 4.25 9 4.25Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="1.25" />
    </svg>
  );
}

function EyeHiddenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M9 4.25c3.25 0 5.75 2.5 6.75 4.75-1 2.25-3.5 4.75-6.75 4.75S3.25 11.25 2.25 9C3.25 6.75 5.75 4.25 9 4.25Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="1.25" />
      {/* Drawn over the eye rather than replacing it, so a hidden row still
          reads as the same control in a struck-through state. */}
      <path
        d="M3.5 14.5 14.5 3.5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

type TableSettingsMenuProps = {
  columns: HideableColumn[];
  /** Keys currently hidden from the table. */
  hidden: string[];
  onToggle: (key: string) => void;
  onShowAll: () => void;
};

/**
 * The icon button beside the Ad type filter, and the panel it opens: one row per
 * hideable column, each toggling that column's eye.
 *
 * Anchored rather than portaled. The panel belongs to the button, and the card
 * header it sits in doesn't clip, so an absolutely positioned child stays put
 * on scroll without any of the measuring a portal would need.
 *
 * V3 uses InsightsFilterMenu for Essential / All columns instead.
 */
export function TableSettingsMenu({
  columns,
  hidden,
  onToggle,
  onShowAll,
}: TableSettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const allVisible = hidden.length === 0;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-label="Table settings"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
        className={`flex size-8 items-center justify-center rounded-full border transition-colors ${
          open
            ? "border-[#333] text-[#333]"
            : "border-[#dfe0e1] text-[#757575] hover:border-[#999] hover:text-[#333]"
        }`}
      >
        <SlidersIcon />
      </button>

      {open ? (
        <div
          id={panelId}
          className="bg-fs-surface-primary absolute top-full right-0 z-50 mt-2 w-[268px] rounded-lg border border-[#dfe0e1] py-3 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
        >
          <div className="flex items-center justify-between gap-4 px-4 pb-1">
            <p className="text-sm leading-5 font-medium text-[#333]">
              Table settings
            </p>
            {/* Disabled rather than hidden once everything is showing: the
                control is the way back from any hidden column, so it shouldn't
                appear and disappear as rows are toggled. */}
            <button
              type="button"
              disabled={allVisible}
              onClick={onShowAll}
              className={`text-sm leading-5 underline ${
                allVisible
                  ? "text-[#b3b3b3]"
                  : "text-[#333] hover:text-(--slate-color-grey-1000)"
              }`}
            >
              Show all
            </button>
          </div>
          {columns.map((column) => {
            const isHidden = hidden.includes(column.key);
            return (
              <button
                key={column.key}
                type="button"
                aria-pressed={!isHidden}
                onClick={() => onToggle(column.key)}
                className="flex w-full items-center justify-between gap-4 px-4 py-2 text-left hover:bg-[#fafafa]"
              >
                <span
                  className={`text-sm leading-5 ${
                    isHidden ? "text-[#757575]" : "text-[#333]"
                  }`}
                >
                  {column.label}
                </span>
                <span
                  className={isHidden ? "text-[#757575]" : "text-[#333]"}
                  aria-hidden
                >
                  {isHidden ? <EyeHiddenIcon /> : <EyeIcon />}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
